import * as React from "react";
import "../presenter.scss";
import Transaction, {BankAccountType, ReconciliationStatus} from "../../../../../model/transaction/Transaction";
import ReconciliationModal from "./ReconciliationModal";
import {PaymentMethod} from "../../../../../model/payment/Payment";
import {api} from "../../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import {TransactionApiDto} from "../../../../../api/model/rest";
import BillReconciliationModal from "./BillReconciliationModal";

export interface ReconciliationValue {
	reconcileStatus: keyof typeof ReconciliationStatus;
	matched: boolean;
}

interface IProps {
	/**
	 * Cell value.
	 */
	value: ReconciliationValue;
	/**
	 * Transaction dto.
	 */
	transactionData: TransactionApiDto;
	/**
	 * Reconciliation modal open callback.
	 */
	onModalOpen: () => void;
	/**
	 * Reconciliation modal close callback.
	 */
	onModalClose: () => void;
}

export const ReconciliationStatusPresenter: React.SFC<IProps> = (props) => {
	return (
		<ReconciliationStatusPresenterComponent key={props.transactionData.id}
												{...props}
												transaction={Transaction.fromJson(props.transactionData)}/>
	);
};

interface IPresenterProps {
	/**
	 * Cell value.
	 */
	value: ReconciliationValue;
	/**
	 * Transaction.
	 */
	transaction: Transaction;
	/**
	 * Reconciliation modal open callback.
	 */
	onModalOpen: () => void;
	/**
	 * Reconciliation modal close callback.
	 */
	onModalClose: () => void;
}

interface IState {
	/**
	 * Indicates if reconciliation modal is displayed.
	 */
	showReconciliationModal: boolean
	/**
	 * Transaction to be reconciled.
	 */
	transaction: Transaction;
}

export class ReconciliationStatusPresenterComponent extends React.Component<IPresenterProps, IState> {
	constructor(props: IPresenterProps) {
		super(props);
		this.state = {showReconciliationModal: false, transaction: props.transaction};
	}

	render() {
		return (
			<div className="presenter-reconciliation">
				{this.renderStatusButton()}
				{this.state.showReconciliationModal && this.renderReconciliationModal()}
			</div>
		);
	}

	fetchTransaction = (transactionId: string) => api.instance.transactionApi.fetchTransaction(transactionId)
		.then((transaction) => this.setState({transaction}))
		.catch((error) => notifyBugsnagAndShowToast(error, "Fetch transaction"));

	renderStatusButton = () => {
		switch (this.props.value.reconcileStatus) {
			case ReconciliationStatus.RECONCILED:
				return (
					<div className="presenter-reconciliation-reconciled-label">Reconciled</div>
				);
			case ReconciliationStatus.UNRECONCILED:
				if (this.state.transaction.paymentMethod === PaymentMethod.STRIPE_PAYOUT) {
					return (
						<div className="presenter-reconciliation_link">Multiple ››</div>
					);
				}
				const isMatchedAndPaidByStripe = this.props.value.matched &&
					this.state.transaction.paymentMethod === PaymentMethod.CARD;
				const buttonLabel = isMatchedAndPaidByStripe ? "Mark paid" : "Reconcile";
				return (
					<div>
						<div onClick={this.openModal} className="presenter-reconciliation-button">{buttonLabel}</div>
						{this.props.value.matched && <div className="presenter-reconciliation-match-indicator">!</div>}
					</div>
				);
			case ReconciliationStatus.PARTIALLY_RECONCILED:
				const onClick = this.state.transaction.paymentMethod !== PaymentMethod.STRIPE_PAYOUT ? this.openModal : null;
				return (
					<div onClick={onClick} className="presenter-reconciliation_link">Partially reconciled ››</div>
				);
			case ReconciliationStatus.IGNORED:
				return (
					<div className="presenter-reconciliation-ignored-label">Ignored</div>
				);
		}
	};

	renderReconciliationModal = () => {
		const ModalComponent = this.props.transaction.bankAccountType === BankAccountType.TRUST_ACCOUNT ? ReconciliationModal :
			BillReconciliationModal;
		return <ModalComponent onClose={this.closeModal}
							   transaction={this.state.transaction}
							   onReconciliationSuccess={this.onReconciliationSuccess}/>;
	};

	openModal = (evt: React.MouseEvent<HTMLSpanElement>) => {
		evt.stopPropagation();
		this.setState({showReconciliationModal: true}, this.props.onModalOpen);
	};

	closeModal = () => {
		api.instance.invoiceApi.clearInvoicesCache();
		this.setState({showReconciliationModal: false}, this.props.onModalClose);
	};

	onReconciliationSuccess = () => {
		this.fetchTransaction(this.state.transaction.id);
		api.instance.invoiceApi.clearInvoicesCache();
	};
}
